import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute, GuestRoute } from '@/components/auth/ProtectedRoute';
import { ROUTES } from '@/lib/constants';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/Home/HomePage'));
const FarmsPage = lazy(() => import('@/pages/Farms/FarmsPage'));
const FarmDetailPage = lazy(() => import('@/pages/Farms/FarmDetailPage'));
const RegionsPage = lazy(() => import('@/pages/Regions/RegionsPage'));
const RegionDetailPage = lazy(() => import('@/pages/Regions/RegionDetailPage'));
const MapPage = lazy(() => import('@/pages/Map/MapPage'));
const RoutePlannerPage = lazy(() => import('@/pages/Routes/RoutePlannerPage'));
const LoginPage = lazy(() => import('@/pages/Auth/LoginPage'));
const SignupPage = lazy(() => import('@/pages/Auth/SignupPage'));
const VerifyEmailPage = lazy(() => import('@/pages/Auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/Auth/ResetPasswordPage'));
const ProfilePage = lazy(() => import('@/pages/User/ProfilePage'));
const MyListsPage = lazy(() => import('@/pages/User/MyListsPage'));
const CompleteProfilePage = lazy(() => import('@/pages/User/CompleteProfilePage'));
const NotFoundPage = lazy(() => import('@/pages/Error/NotFoundPage'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Spinner size="lg" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes with app layout */}
          <Route element={<AppLayout />}>
            <Route path={ROUTES.home} element={<HomePage />} />
            <Route path={ROUTES.farms} element={<FarmsPage />} />
            <Route path="/farms/:farmId" element={<FarmDetailPage />} />
            <Route path={ROUTES.regions} element={<RegionsPage />} />
            <Route path="/regions/:regionId" element={<RegionDetailPage />} />
            <Route path={ROUTES.map} element={<MapPage />} />
            <Route path={ROUTES.routePlanner} element={<RoutePlannerPage />} />
            
            {/* Protected user routes */}
            <Route
              path={ROUTES.profile}
              element={
                <ProtectedRoute requireProfile>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.myLists}
              element={
                <ProtectedRoute requireProfile>
                  <MyListsPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Auth routes (no app layout) - guest only */}
          <Route
            path={ROUTES.login}
            element={
              <GuestRoute>
                <LoginPage />
              </GuestRoute>
            }
          />
          <Route
            path={ROUTES.signup}
            element={
              <GuestRoute>
                <SignupPage />
              </GuestRoute>
            }
          />
          <Route path={ROUTES.verifyEmail} element={<VerifyEmailPage />} />
          <Route
            path={ROUTES.forgotPassword}
            element={
              <GuestRoute>
                <ForgotPasswordPage />
              </GuestRoute>
            }
          />
          <Route
            path={ROUTES.resetPassword}
            element={
              <GuestRoute>
                <ResetPasswordPage />
              </GuestRoute>
            }
          />

          {/* Complete profile - requires auth but not completed profile */}
          <Route
            path={ROUTES.completeProfile}
            element={
              <ProtectedRoute>
                <CompleteProfilePage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
